import React, { useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import styled from '@emotion/styled';
import { useHistory, useLocation } from 'react-router-dom';
import { breakpoint_small, breakpoint_large, breakpoint_macbook_small, breakpoint_macbook, breakpoint_macbook_large } from '../../../../../shared/constants/breakpoints';
import {
    DashboardWrapper,
    LeftSidePanel,
    MainContent,
    MainContentLoaderWrapper,
    DatePickerSection,
    LocationsSection,
    LocationsHeaderWrapper,
    LocationsTitle,
    SelectionControls,
    SelectionButton,
    SelectionDivider,
    AssistantListToolbar,
    AssistantSearchWrapper,
    AssistantSearchInput,
    AssistantSearchClear,
    LocationsScrollContainer,
    EmptyStateMessage,
    OrgAvatarSection,
    OrgName,
    VerticalAccentDecoration,
    AvatarScaleWrapper,
    BottomGraphic
} from '../../basicOrganizationApplications/components/ApplicationDashboard.styles';
import DashboardDatePicker from '../../basicOrganizationApplications/components/DashboardDatePicker';
import AnalyticsInterval from '../../../../../shared/models/analytics/AnalyticsInterval';
import PlatformOrganizationItem, { PlatformOrganizationItemModel } from './PlatformOrganizationItem';
import { AppNameMap, EntityNameMap } from '../../../../../shared/utils/engagementAnalyticsTransformers';
import { BaseDashboardProps } from '../../basicOrganizationApplications/components/dashboardTypes';
import Loader from '../../../../../shared/components/general/Loader';
import PageError from '../../../../../shared/components/general/PageError';
import PlatformAvatarUpload from './PlatformAvatarUpload';
import PlatformHealthDashboard from './PlatformHealthDashboard';
import {
    getAvailablePlatformDashboardViews,
    getPlatformDashboardView,
    PlatformDashboardView,
    shouldOpenInitialPlatformDateModal
} from '../platformDashboardView';

type PlatformVariant = 'ordering' | 'scheduling';

interface PlatformDashboardShellProps {
    title: string;
    variant: PlatformVariant;
    organizations: PlatformOrganizationItemModel[];
    managementOrganizations?: PlatformOrganizationItemModel[];
    appNameMap: AppNameMap;
    selectedAppLiveStatusMap?: Record<string, boolean>;
    entityNameMap: EntityNameMap;
    representativeAppId?: string;
    platformAppIdsByOrganizationId: Record<string, string[]>;
    isOutboundCallApp?: boolean;
    loadErrors?: string[];
    discoveryProgress?: {
        loadedCount: number;
        totalCount: number;
        isComplete: boolean;
        hasErrors: boolean;
    };
    onReload?: () => void;
    isLoading: boolean;
    canEditHealthThresholds: boolean;
    currentUserEmail: string;
    DashboardComponent: React.ComponentType<BaseDashboardProps>;
    ManagementComponent?: React.ComponentType<{
        organizations: PlatformOrganizationItemModel[];
        onProcessingChange: (isProcessing: boolean) => void;
        onReload?: () => void;
        discoveryProgress?: {
            loadedCount: number;
            totalCount: number;
            isComplete: boolean;
            hasErrors: boolean;
        };
    }>;
}

const PlatformDashboardShell: React.FC<PlatformDashboardShellProps> = ({
    title,
    variant,
    organizations,
    managementOrganizations = organizations,
    appNameMap,
    selectedAppLiveStatusMap,
    entityNameMap,
    representativeAppId,
    platformAppIdsByOrganizationId,
    isOutboundCallApp = false,
    loadErrors = [],
    discoveryProgress = { loadedCount: 0, totalCount: 0, isComplete: true, hasErrors: false },
    onReload,
    isLoading,
    canEditHealthThresholds,
    currentUserEmail,
    DashboardComponent,
    ManagementComponent
}) => {
    const history = useHistory();
    const location = useLocation();
    const getInitialLastWeekRange = () => ({
        startDate: moment().subtract(1, 'week').startOf('week').format('YYYY-MM-DD'),
        endDate: moment().subtract(1, 'week').endOf('week').format('YYYY-MM-DD'),
        interval: 'week' as AnalyticsInterval
    });
    const initialLastWeekRange = getInitialLastWeekRange();
    const [startDate, setStartDate] = useState<string>('');
    const [endDate, setEndDate] = useState<string>('');
    const [interval, setInterval] = useState<AnalyticsInterval>('custom');
    const [modalStartDate, setModalStartDate] = useState<string>(initialLastWeekRange.startDate);
    const [modalEndDate, setModalEndDate] = useState<string>(initialLastWeekRange.endDate);
    const [modalInterval, setModalInterval] = useState<AnalyticsInterval>(initialLastWeekRange.interval);
    const [hasConfirmedInitialDateRange, setHasConfirmedInitialDateRange] = useState(false);
    const [excludedOrganizationIds, setExcludedOrganizationIds] = useState<Set<string>>(new Set());
    const [searchQuery, setSearchQuery] = useState('');
    const [isDashboardLoading, setIsDashboardLoading] = useState(false);
    const [refreshKey, setRefreshKey] = useState(0);
    const [avatarUploadErrors, setAvatarUploadErrors] = useState<string[]>([]);
    const [activeView, setActiveView] = useState<PlatformDashboardView>(() =>
        getPlatformDashboardView(location.pathname, Boolean(ManagementComponent))
    );
    const [isManagementProcessing, setIsManagementProcessing] = useState(false);

    useEffect(() => {
        const isManagementRoute = location.pathname.toLowerCase().endsWith('/management');
        if (isManagementRoute && !ManagementComponent) {
            history.replace(`/v/platform/${variant}`);
            return;
        }
        setActiveView(getPlatformDashboardView(location.pathname, Boolean(ManagementComponent)));
    }, [location.pathname, ManagementComponent, history, variant]);

    useEffect(() => {
        setExcludedOrganizationIds(new Set(organizations.filter(org => !org.hasLiveApp).map(org => org.id)));
    }, [organizations, variant]);

    const filteredOrganizations = useMemo(() => {
        return organizations.filter(org => org.name.toLowerCase().includes(searchQuery.toLowerCase()));
    }, [organizations, searchQuery]);

    const selectedOrganizationIds = useMemo(() => new Set(
        organizations.filter(org => !excludedOrganizationIds.has(org.id)).map(org => org.id)
    ), [organizations, excludedOrganizationIds]);

    const organizationIds = useMemo(() => {
        return organizations.map(org => org.id);
    }, [organizations]);

    const selectedAppIds = useMemo(() => {
        const ids = new Set<string>();
        Array.from(selectedOrganizationIds).forEach(orgId => {
            (platformAppIdsByOrganizationId[orgId] || []).forEach(appId => ids.add(appId));
        });
        return ids;
    }, [selectedOrganizationIds, platformAppIdsByOrganizationId]);

    const isMultiLingual = useMemo(() => {
        return organizations.some(org => selectedOrganizationIds.has(org.id) && org.isMultiLingual);
    }, [organizations, selectedOrganizationIds]);

    const hasActiveDateRange = Boolean(startDate && endDate);
    const availableViews = getAvailablePlatformDashboardViews(Boolean(ManagementComponent));

    const handleDateRangeChange = (newStartDate: string, newEndDate: string, newInterval: AnalyticsInterval) => {
        setStartDate(newStartDate);
        setEndDate(newEndDate);
        setInterval(newInterval);
    };

    const handleInitialDateRangeChange = (newStartDate: string, newEndDate: string, newInterval: AnalyticsInterval) => {
        setModalStartDate(newStartDate);
        setModalEndDate(newEndDate);
        setModalInterval(newInterval);
        handleDateRangeChange(newStartDate, newEndDate, newInterval);
        setHasConfirmedInitialDateRange(true);
    };

    const toggleOrganization = (organizationId: string) => {
        setExcludedOrganizationIds(prev => {
            const next = new Set(prev);
            if (next.has(organizationId)) {
                next.delete(organizationId);
            } else {
                next.add(organizationId);
            }
            return next;
        });
    };

    const selectAll = () => setExcludedOrganizationIds(new Set());
    const deselectAll = () => setExcludedOrganizationIds(new Set(organizations.map(org => org.id)));

    const handleRefresh = () => {
        setRefreshKey(prev => prev + 1);
        onReload?.();
    };

    const navigateToView = (view: PlatformDashboardView) => {
        if (isManagementProcessing || (view === 'management' && !ManagementComponent)) return;
        history.push(view === 'analytics'
            ? `/v/platform/${variant}`
            : `/v/platform/${variant}/${view}`);
    };

    const dateRange = hasActiveDateRange
        ? `${moment(startDate).format('MMM D')} - ${moment(endDate).format('MMM D')}`
        : 'Select a date range';
    const totalLiveLocations = useMemo(() => organizations.reduce((sum, org) => sum + (org.liveAppCount || 0), 0), [organizations]);
    const avatarStorageKey = `platform-dashboard-avatar-${variant}`;
    const isInitialModalOpen = shouldOpenInitialPlatformDateModal(
        isLoading,
        organizations.length,
        hasConfirmedInitialDateRange,
        activeView
    );
    const isBusy = isLoading || isDashboardLoading || isManagementProcessing;

    const clearAvatarUploadErrors = () => {
        setAvatarUploadErrors([]);
    };

    const dashboardProps = useMemo((): BaseDashboardProps => ({
        organizationId: organizations[0]?.id || '',
        organizationIds,
        selectedEntityIds: selectedOrganizationIds,
        selectedAppIds,
        appNameMap,
        selectedAppLiveStatusMap,
        entityNameMap,
        representativeAppId,
        platformAppIdsByOrganizationId,
        isOutboundCallApp,
        analyticsMode: 'platform',
        dateRange,
        startDate,
        endDate,
        shouldLoadData: hasConfirmedInitialDateRange,
        isChartsLoading: false,
        totalAppCount: organizations.length,
        isMultiLingual,
        onLoadingChange: setIsDashboardLoading,
        refreshKey,
        isPlatformOperator: canEditHealthThresholds
    }), [
        canEditHealthThresholds,
        organizations,
        organizationIds,
        selectedOrganizationIds,
        selectedAppIds,
        appNameMap,
        selectedAppLiveStatusMap,
        entityNameMap,
        representativeAppId,
        platformAppIdsByOrganizationId,
        isOutboundCallApp,
        dateRange,
        startDate,
        endDate,
        hasConfirmedInitialDateRange,
        isMultiLingual,
        refreshKey
    ]);

    return (
        <PlatformDashboardWrapper>
            <PageError errors={avatarUploadErrors} onClear={clearAvatarUploadErrors} />
            <PageError errors={loadErrors} type="warning" />
            {activeView !== 'management' && <LeftSidePanel isCollapsed={false}>
                <OrgAvatarSection $variant={variant === 'ordering' ? 'food-ordering' : 'healthcare'}>
                    <VerticalAccentDecoration $variant={variant === 'ordering' ? 'food-ordering' : 'healthcare'} />
                    <AvatarScaleWrapper>
                        <PlatformAvatarUpload
                            storageKey={avatarStorageKey}
                            displayName={title}
                            disabled={isLoading || isDashboardLoading}
                            onError={setAvatarUploadErrors}
                            onClearError={clearAvatarUploadErrors}
                        />
                    </AvatarScaleWrapper>
                    <OrgName>{title}</OrgName>
                    <LiveLocationCountPill>
                        {totalLiveLocations.toLocaleString()} Live Locations
                    </LiveLocationCountPill>
                </OrgAvatarSection>

                <DatePickerSection>
                    <DashboardDatePicker
                        startDate={hasActiveDateRange ? startDate : modalStartDate}
                        endDate={hasActiveDateRange ? endDate : modalEndDate}
                        interval={hasActiveDateRange ? interval : modalInterval}
                        onChange={handleDateRangeChange}
                        isDisabled={isLoading || isDashboardLoading || !hasConfirmedInitialDateRange}
                    />
                </DatePickerSection>

                <LocationsSection $isCollapsed={false}>
                    <LocationsHeaderWrapper>
                        <LocationsTitle>Organizations ({selectedOrganizationIds.size})</LocationsTitle>
                        <SelectionControls>
                            <SelectionButton onClick={selectAll} disabled={isBusy}>Select All</SelectionButton>
                            <SelectionDivider>|</SelectionDivider>
                            <SelectionButton onClick={deselectAll} disabled={isBusy}>Deselect All</SelectionButton>
                            <SelectionDivider>|</SelectionDivider>
                            <SelectionButton onClick={handleRefresh} disabled={isBusy}>Refresh</SelectionButton>
                        </SelectionControls>
                    </LocationsHeaderWrapper>

                    <AssistantListToolbar>
                        <AssistantSearchWrapper $isDisabled={isBusy}>
                            <AssistantSearchInput
                                type="text"
                                placeholder="Search organizations..."
                                value={searchQuery}
                                onChange={e => setSearchQuery(e.target.value)}
                                disabled={isBusy}
                            />
                            {searchQuery && <AssistantSearchClear onClick={() => setSearchQuery('')}>x</AssistantSearchClear>}
                        </AssistantSearchWrapper>
                    </AssistantListToolbar>

                    <LocationsScrollContainer>
                        {filteredOrganizations.map(org => (
                            <PlatformOrganizationItem
                                key={org.id}
                                organization={org}
                                isSelected={!excludedOrganizationIds.has(org.id)}
                                disabled={isDashboardLoading || isManagementProcessing}
                                onToggleFilter={() => toggleOrganization(org.id)}
                            />
                        ))}
                    </LocationsScrollContainer>
                </LocationsSection>

                <BottomGraphic />
            </LeftSidePanel>}

            <PlatformMainContent data-pdf-content $isCollapsed={false}>
                <ViewTabs>
                    {availableViews.map(view => (
                        <ViewTab key={view} active={activeView === view} disabled={isManagementProcessing} onClick={() => navigateToView(view)}>
                            {{ analytics: 'Analytics', health: 'Health', management: 'Management' }[view]}
                        </ViewTab>
                    ))}
                </ViewTabs>
                {isLoading && (
                    <MainContentLoaderWrapper>
                        <Loader />
                    </MainContentLoaderWrapper>
                )}
                {!isLoading && organizations.length === 0 && discoveryProgress.isComplete && activeView === 'analytics' && (
                    <EmptyStateMessage>
                        <p>No matching organizations were found for this dashboard.</p>
                    </EmptyStateMessage>
                )}
                {!isLoading && organizations.length > 0 && activeView === 'analytics' && (
                    <PlatformDashboardContent>
                        <DashboardComponent {...dashboardProps} />
                    </PlatformDashboardContent>
                )}
                {!isLoading && activeView === 'health' && (
                    <PlatformDashboardContent>
                        <PlatformHealthDashboard
                            variant={variant}
                            organizations={organizations}
                            selectedOrganizationIds={selectedOrganizationIds}
                            startDate={startDate}
                            endDate={endDate}
                            shouldLoadData={hasConfirmedInitialDateRange}
                            canEditThresholds={canEditHealthThresholds}
                            currentUserEmail={currentUserEmail}
                            refreshKey={refreshKey}
                            onLoadingChange={setIsDashboardLoading}
                        />
                    </PlatformDashboardContent>
                )}
                {!isLoading && activeView === 'management' && ManagementComponent && (
                    <PlatformDashboardContent>
                        <ManagementComponent
                            organizations={managementOrganizations}
                            onProcessingChange={setIsManagementProcessing}
                            onReload={onReload}
                            discoveryProgress={discoveryProgress}
                        />
                    </PlatformDashboardContent>
                )}
            </PlatformMainContent>

            <DashboardDatePicker
                startDate={modalStartDate}
                endDate={modalEndDate}
                interval={modalInterval}
                onChange={handleInitialDateRangeChange}
                mode="modal"
                isOpen={isInitialModalOpen}
                allowDismiss={false}
                modalTitle={`Choose a date range for ${title}`}
                modalDescription="Select the reporting window before platform data loads. The sidebar picker will stay available after this first selection."
                applyButtonLabel="Load Dashboard"
            />
        </PlatformDashboardWrapper>
    );
};

const PlatformDashboardWrapper = styled(DashboardWrapper)`
    width: 100%;
    height: 100%;
    min-width: 0;
    min-height: 0;

    ${breakpoint_macbook_small} {
        width: 100%;
        height: 100%;
        min-width: 0;
    }

    ${breakpoint_macbook} {
        width: 100%;
        height: 100%;
        min-width: 0;
    }

    ${breakpoint_macbook_large} {
        width: 100%;
        height: 100%;
        min-width: 0;
    }

    ${breakpoint_large} {
        width: 100%;
        height: 100%;
        min-width: 0;
    }

    ${breakpoint_small} {
        width: 100%;
        height: 100%;
        min-width: 0;
        min-height: auto;
    }
`;

const PlatformMainContent = styled(MainContent)`
    height: 100%;
    min-width: 0;
    min-height: 0;
    display: flex;
    flex-direction: column;

    ${breakpoint_macbook_small} {
        min-width: 0;
        min-height: 0;
    }

    ${breakpoint_macbook} {
        min-width: 0;
        min-height: 0;
    }

    ${breakpoint_macbook_large} {
        min-width: 0;
        min-height: 0;
    }

    ${breakpoint_large} {
        min-width: 0;
        min-height: 0;
    }

    ${breakpoint_small} {
        min-width: 0;
        min-height: auto;
    }
`;

const PlatformDashboardContent = styled.div`
    flex: 1;
    width: 100%;
    height: 100%;
    min-width: 0;
    min-height: 0;
    display: flex;
    flex-direction: column;

    ${breakpoint_macbook_small} {
        min-width: 0;
        min-height: 0;
    }

    ${breakpoint_macbook} {
        min-width: 0;
        min-height: 0;
    }

    ${breakpoint_macbook_large} {
        min-width: 0;
        min-height: 0;
    }

    ${breakpoint_large} {
        min-width: 0;
        min-height: 0;
    }

    ${breakpoint_small} {
        width: 100%;
        min-height: auto;
    }
`;

const ViewTabs = styled.div`
    display: flex;
    flex-shrink: 0;
    gap: 8px;
    padding: 12px 24px 0;
    background: #fff;
    border-bottom: 1px solid #e5e9ef;
`;

const ViewTab = styled.button<{ active: boolean }>`
    appearance: none;
    border: 0;
    border-bottom: 3px solid ${props => props.active ? '#1c6697' : 'transparent'};
    background: transparent;
    color: ${props => props.active ? '#1c6697' : '#52657a'};
    padding: 10px 16px;
    font-family: Muli, sans-serif;
    font-size: 14px;
    font-weight: 700;
    cursor: pointer;

    &:disabled { cursor: not-allowed; opacity: 0.55; }
`;

const LiveLocationCountPill = styled.div`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    margin: -8px 0 16px 0;
    padding: 8px 14px;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.86);
    border: 1px solid rgba(203, 204, 210, 0.9);
    color: #324661;
    font-family: Muli;
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.02em;
    box-shadow: 0 10px 30px rgba(50, 70, 97, 0.08);
    backdrop-filter: blur(8px);

    ${breakpoint_macbook_small} {
        margin: -4px 0 10px 0;
        padding: 6px 10px;
        font-size: 10px;
    }

    ${breakpoint_large} {
        margin: -2px 0 8px 0;
        padding: 5px 8px;
        font-size: 9px;
    }
`;

export default PlatformDashboardShell;
